import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

import { Button, type ButtonProps } from "./Button";
import { useMobile } from "../../lib/useMediaQuery";

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

// Open overlays, innermost last. Only the top one answers Esc and keeps Tab
// inside itself, so a confirm dialog over a drawer closes alone and the drawer
// stays put.
const stack: symbol[] = [];

// The page behind an overlay must not scroll, and on a desktop with a classic
// scrollbar hiding it would shift the layout; the gutter is kept as padding.
let locks = 0;
let savedPadding = "";
function lockScroll() {
  if (locks++ > 0) return;
  const gutter = window.innerWidth - document.documentElement.clientWidth;
  savedPadding = document.body.style.paddingRight;
  if (gutter > 0) document.body.style.paddingRight = `${gutter}px`;
  document.documentElement.style.overflow = "hidden";
}
function unlockScroll() {
  if (--locks > 0) return;
  document.documentElement.style.overflow = "";
  document.body.style.paddingRight = savedPadding;
}

const reducedMotion = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Back closes the top overlay. Each open overlay owns one history entry (same
// URL, our marker in its state); Android's back button and iOS's edge swipe
// pop it and the overlay closes instead of the page going away under it.
// history.back() is asynchronous, so an overlay that leaves without a back
// press (a save that unmounts it, a dialog swapped for the next one) removes
// its entry and the next overlay waits for that pop before adding its own,
// or it would take the pop for a back press and close at once.
const MARK = "rpOverlay";

interface Entry {
  key: string;
  dead: boolean;
  onBack: () => void;
}

/** Overlay entries in history order, innermost last. */
const live: Entry[] = [];
let expectedPops = 0;
let settling: Promise<void> | null = null;
let settle: (() => void) | null = null;
let listening = false;

function onPopState() {
  if (expectedPops > 0) {
    expectedPops = 0;
    settle?.();
    return;
  }
  const current = (history.state as Record<string, unknown> | null)?.[MARK] as string | undefined;
  const at = current ? live.findIndex((e) => e.key === current) : -1;
  // Forward onto an overlay that has since closed: nothing to undo.
  if (current && at === -1) return;
  const popped = live.splice(at + 1);
  for (let i = popped.length - 1; i >= 0; i--) if (!popped[i].dead) popped[i].onBack();
}

function pushEntry(entry: Entry) {
  if (!listening) {
    window.addEventListener("popstate", onPopState);
    listening = true;
  }
  // React Router's own state (key, idx) is carried over, so the router sees
  // the same location when the entry is popped and does not re-navigate.
  history.pushState({ ...((history.state as object | null) ?? {}), [MARK]: entry.key }, "");
  live.push(entry);
}

function releaseEntry(key: string) {
  const entry = live.find((e) => e.key === key);
  if (!entry) return; // already popped by a back press
  entry.dead = true;
  // An overlay still open above this one keeps the entries; its own release
  // will take them all.
  if (!live[live.length - 1].dead) return;
  const removed: Entry[] = [];
  while (live.length && live[live.length - 1].dead) removed.push(live.pop()!);
  // Step back only if the page still shows the entry we added; if the app
  // navigated in the meantime, going back would undo that navigation.
  if ((history.state as Record<string, unknown> | null)?.[MARK] !== removed[0].key) return;
  expectedPops = 1;
  settling = new Promise<void>((resolve) => {
    const finish = () => {
      settling = null;
      settle = null;
      resolve();
    };
    settle = finish;
    // A pop that never arrives must not strand the next overlay.
    window.setTimeout(() => settle === finish && ((expectedPops = 0), finish()), 400);
  });
  history.go(-removed.length);
}

/**
 * What every modal surface owes the page: focus moves in on open and back to
 * the trigger on close, Tab cycles inside, Esc closes the top-most overlay
 * only, the page behind stops scrolling. `close` plays the exit animation
 * before telling the parent, so unmounts are never abrupt.
 */
function useOverlay(onClose: () => void, exitMs: number) {
  const panel = useRef<HTMLDivElement>(null);
  const [closing, setClosing] = useState(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const closingRef = useRef(false);

  const close = useCallback(() => {
    if (closingRef.current) return;
    closingRef.current = true;
    setClosing(true);
    window.setTimeout(() => onCloseRef.current(), reducedMotion() ? 0 : exitMs);
  }, [exitMs]);

  useEffect(() => {
    const id = Symbol("overlay");
    stack.push(id);
    const el = panel.current;
    const previous = document.activeElement as HTMLElement | null;
    lockScroll();

    let alive = true;
    const entry: Entry = { key: Math.random().toString(36).slice(2), dead: false, onBack: close };
    const arm = () => alive && pushEntry(entry);
    if (settling) void settling.then(arm);
    else arm();
    // A field with autoFocus already took focus during commit; otherwise the
    // panel itself, so the next Tab lands on its first control.
    if (el && !el.contains(document.activeElement)) el.focus({ preventScroll: true });

    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== id) return;
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== "Tab" || !el) return;
      const items = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((n) => n.offsetParent !== null);
      if (items.length === 0) {
        e.preventDefault();
        el.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === el)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      } else if (!el.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      alive = false;
      releaseEntry(entry.key);
      document.removeEventListener("keydown", onKey);
      stack.splice(stack.indexOf(id), 1);
      unlockScroll();
      previous?.focus?.({ preventScroll: true });
    };
  }, [close]);

  return { panel, closing, close };
}

const CloseContext = createContext<() => void>(() => {});

/** The animated close of the nearest Dialog or Drawer, for buttons in its footer. */
export const useOverlayClose = () => useContext(CloseContext);

/** A footer button that closes the surrounding overlay the same way Esc does. */
export function CloseAction({ children = "Anuluj", ...props }: Omit<ButtonProps, "onClick">) {
  const close = useOverlayClose();
  return (
    <Button {...props} onClick={close}>
      {children}
    </Button>
  );
}

function CloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Zamknij"
      className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-sm bg-glass-2 text-lg leading-none text-muted transition hover:text-ink focus-visible:outline-none focus-visible:shadow-glow"
    >
      ×
    </button>
  );
}

const stop = (e: React.MouseEvent) => e.stopPropagation();

/** Centred modal: dimmed blurred backdrop, raised panel. Esc / backdrop closes. */
export function Dialog({
  title,
  subtitle,
  onClose,
  children,
  footer,
  width = 440,
}: {
  title?: string;
  subtitle?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  // A phone gets a bottom sheet: full width, thumb-reachable footer, safe-area
  // padding. Desktop keeps the centred modal.
  const sheet = useMobile();
  const { panel, closing, close } = useOverlay(onClose, sheet ? 180 : 140);
  return (
    <CloseContext.Provider value={close}>
      <div
        onClick={close}
        className={`fixed inset-0 z-50 flex justify-center bg-[rgba(4,4,8,0.7)] backdrop-blur-[6px] ${closing ? "rp-exit-fade" : "rp-enter-fade"} ${
          sheet ? "items-end" : "items-center p-4"
        }`}
      >
        <div
          ref={panel}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={stop}
          className={`flex w-full flex-col overflow-hidden border border-line-strong bg-panel shadow-float outline-none ${
            sheet
              ? `max-h-[92dvh] rounded-t-[20px] border-b-0 pb-[env(safe-area-inset-bottom)] ${closing ? "rp-exit-sheet" : "rp-enter-sheet"}`
              : `max-h-[calc(100vh-32px)] rounded-lg ${closing ? "rp-exit-pop" : "rp-enter-pop"}`
          }`}
          style={{ maxWidth: sheet ? undefined : width }}
        >
          {sheet && <span aria-hidden="true" className="mx-auto mt-2.5 h-1 w-9 rounded-full bg-line-strong" />}
          <header className="flex items-start justify-between gap-3 px-5 pt-[18px]">
            <div>
              {title && <h2 className="text-[17px] font-semibold tracking-[-0.015em]">{title}</h2>}
              {subtitle && <p className="mt-[3px] text-xs text-muted">{subtitle}</p>}
            </div>
            <CloseButton onClick={close} />
          </header>
          <div className="overflow-y-auto overscroll-contain p-5">{children}</div>
          {footer && (
            <footer className={`gap-2 px-5 pb-5 ${sheet ? "flex flex-col-reverse [&>button]:h-11 [&>button]:w-full" : "flex justify-end"}`}>{footer}</footer>
          )}
        </div>
      </div>
    </CloseContext.Provider>
  );
}

/** Destructive / irreversible actions: one question, one consequence, the verb on the button. */
export function ConfirmDialog({
  title,
  body,
  confirmLabel = "Potwierdź",
  danger = false,
  pending = false,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  pending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      title={title}
      onClose={onCancel}
      width={400}
      footer={
        <>
          <CloseAction disabled={pending} />
          <Button
            variant={danger ? "danger" : "primary"}
            onClick={onConfirm}
            busy={pending}
            className={danger ? "border-transparent bg-danger text-white hover:bg-danger" : ""}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="text-[15px] leading-normal text-ink-2">{body}</p>
    </Dialog>
  );
}

/** Right-side editor panel: pinned `preview` on top, scrolling body, sticky footer. */
export function Drawer({
  title,
  onClose,
  preview,
  children,
  footer,
  width = 560,
}: {
  title: string;
  onClose: () => void;
  preview?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}) {
  const { panel, closing, close } = useOverlay(onClose, 160);
  return (
    <CloseContext.Provider value={close}>
      <div onClick={close} className={`fixed inset-0 z-50 flex justify-end bg-[rgba(4,4,8,0.6)] backdrop-blur-[4px] ${closing ? "rp-exit-fade" : "rp-enter-fade"}`}>
        <div
          ref={panel}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          onClick={stop}
          className={`flex h-full w-full flex-col border-l border-line-strong bg-panel shadow-drawer outline-none ${closing ? "rp-exit-drawer" : "rp-enter-drawer"}`}
          style={{ maxWidth: width }}
        >
          <header className="flex items-center justify-between gap-3 border-b border-line px-[22px] py-4">
            <h2 className="text-[17px] font-semibold tracking-[-0.015em]">{title}</h2>
            <CloseButton onClick={close} />
          </header>
          {preview && (
            <div className="flex shrink-0 justify-center border-b border-line px-[22px] py-5 max-[899px]:py-3 [background:radial-gradient(60%_80%_at_50%_0%,rgba(139,92,246,0.14),transparent_70%)]">
              {preview}
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-[22px]">{children}</div>
          {footer && <footer className="flex gap-2 border-t border-line bg-panel px-[22px] py-3.5 pb-[calc(14px+env(safe-area-inset-bottom))]">{footer}</footer>}
        </div>
      </div>
    </CloseContext.Provider>
  );
}
